import React, { useEffect, useState } from "react";
import DashboardPatient from "../dashboard/DashboardPatient";
import HeadingResume from "../widgets/HeadingResume";
import WalletIcon from "./../../images/wallet-svg.svg";
import OrderCard from "./OrderCard";
import Loading from "../utils/Loading";
import Axios from "axios";
import useUserState from "../hooks/useUserState";
import useBDocTranslator from "../hooks/useBDocTranslator";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Container,
  Grid,
  Fade,
  Paper,
} from "@material-ui/core";
import ReceiptIcon from "@material-ui/icons/Receipt";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

export default function OrdersSummary() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const localState = useUserState();
  const { t } = useBDocTranslator();

  useEffect(() => {
    const userId = localState[0]?.user?.id;
    const jwt = localState[0]?.jwt;

    const fetchOrders = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await Axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/patient/orders/summary?userId=${userId}`,
          {
            headers: { Authorization: "Bearer " + jwt },
          }
        );
        setOrders(response.data);
      } catch (error) {
        console.log(error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (userId && jwt) {
      fetchOrders();
    }
  }, []);

  const handleHelpWhatsAppRedirect = () => {
    window.open(
      `https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_NUMBER}&text=Hola%20soporte%20necesito%20ayuda%20con%20mi%20orden%20de%20servicio%20en%20la%20plataforma%20de%20Telemedicina.`,
      "_blank"
    );
  };

  const handleRetry = () => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await Axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/patient/orders/summary?userId=${localState[0].user?.id}`,
          {
            headers: { Authorization: "Bearer " + localState[0].jwt },
          }
        );
        setOrders(response.data);
      } catch (error) {
        console.log(error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  };

  return (
    <DashboardPatient>
      <HeadingResume
        title={t("Your Orders List")}
        description={t(
          "Here you will find your appointment history with payments made and pending."
        )}
        icon={WalletIcon}
      />

      <Container maxWidth="lg" style={{ padding: "0 16px" }}>
        {/* Loading State */}
        {loading && (
          <Fade in={loading}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="400px"
              textAlign="center"
            >
              <Loading color="#076cd8" />
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                {t("Loading your orders...")}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Error State */}
        {!loading && error && (
          <Fade in={error}>
            <Card className="shadow rounded-sm-custom">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  padding={4}
                >
                  <ErrorOutlineIcon
                    style={{
                      fontSize: 64,
                      marginBottom: 16,
                      color: "#f44336",
                      opacity: 0.7,
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t("Unable to load orders")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: 24 }}
                  >
                    {t(
                      "There was an error loading your orders. Please check your connection and try again."
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRetry}
                    style={{ backgroundColor: "#076cd8" }}
                  >
                    {t("Try Again")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Empty State */}
        {!loading && !error && orders.length === 0 && (
          <Fade in={!loading && !error}>
            <Card className="shadow rounded-sm-custom">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  padding={4}
                >
                  <ReceiptIcon
                    style={{
                      fontSize: 64,
                      marginBottom: 16,
                      opacity: 0.3,
                      color: "#076cd8",
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t("No orders found")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: 24 }}
                  >
                    {t(
                      "You haven't made any service orders yet. Schedule your first appointment to get started."
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    href="/patient/appointments/create"
                    style={{ backgroundColor: "#076cd8" }}
                  >
                    {t("Schedule Appointment")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Orders List */}
        {!loading && !error && orders.length > 0 && (
          <Fade in={!loading && !error}>
            <Box>
              {/* Orders Summary Info */}
              <Box marginBottom={3}>
                <Paper
                  elevation={1}
                  style={{
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #076cd8",
                    borderRadius: "8px",
                    padding: "12px 16px",
                  }}
                >
                  <Typography variant="body2" style={{ color: "#0d47a1" }}>
                    {t("You have")} <strong>{orders.length}</strong>{" "}
                    {orders.length === 1 ? t("order") : t("orders")}{" "}
                    {t("in your history")}.
                  </Typography>
                </Paper>
              </Box>

              {/* Orders Grid */}
              <Grid container spacing={3} justifyContent="flex-start">
                {orders.map((item) => (
                  <Grid item xs={12} sm={6} md={6} lg={4} key={item.id}>
                    <OrderCard
                      serviceName={item.service_type.name}
                      servicePrice={item.price_total}
                      providerName={
                        item.appointment.provider.firstName +
                        " " +
                        item.appointment.provider.lastname
                      }
                      serviceStatus={item.status}
                      apptDate={new Date(
                        item.appointment.appt_date_start
                      ).toLocaleDateString("en-US")}
                      handleShowDetails={handleHelpWhatsAppRedirect}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        )}
      </Container>
    </DashboardPatient>
  );
}
